import type { Coupon } from '@/Coupons/types/coupons';
import type { RecipeIngredient } from '../types/recipes';

/**
 * Matching configuration constants
 */

/** Minimum character length for a word to be considered for matching.
 *  Shorter words are too common and create false positives (e.g., "oil", "can"). */
const MIN_KEYWORD_LENGTH = 4;

/** Minimum length for a word to have its trailing 's' stripped for singular form.
 *  Prevents mangling short words like "gas" -> "ga". */
const MIN_LENGTH_FOR_DEPLURALIZE = 5;

/**
 * Common words that are too generic to match on
 */
const STOPWORDS = new Set([
  // Common cooking terms that appear everywhere
  'fresh', 'dried', 'chopped', 'minced', 'diced', 'sliced', 'ground', 'grated',
  'cooked', 'raw', 'frozen', 'canned', 'organic', 'large', 'small', 'medium',
  'extra', 'virgin', 'light', 'dark', 'whole', 'half', 'quarter',
  // Generic food terms that match non-food products
  'sauce', 'cream', 'spicy', 'hot', 'cold', 'sweet', 'sour', 'savory',
  'crispy', 'crunchy', 'soft', 'hard', 'thick', 'thin', 'plain', 'flavored',
  'powder', 'powders', 'liquid', 'liquids', 'scent', 'scented', 'clean',
  'water', 'salt', 'salts', 'sugar', 'sugars', 'oil', 'oils',
  // Common non-specific words
  'free', 'style', 'type', 'brand', 'original', 'classic', 'homemade',
  'recipe', 'mix', 'blend', 'seasoning', 'spice', 'herb', 'extract',
  'peeled', 'deveined', 'boneless', 'skinless', 'trimmed', 'washed',
  // Prepositions and articles that might slip through
  'with', 'and', 'the', 'for', 'from', 'into', 'your', 'each', 'any', 'all',
]);

/**
 * Food keywords that are specific enough to match on
 * These are the primary ingredients we want to match
 */
const FOOD_KEYWORDS = new Set([
  // Proteins
  'shrimp', 'shrimps', 'chicken', 'beef', 'pork', 'salmon', 'tuna', 'turkey', 'bacon',
  'sausage', 'sausages', 'ham', 'steak', 'steaks', 'lamb', 'fish', 'crab', 'lobster',
  'scallop', 'scallops', 'meatball', 'meatballs', 'ground beef', 'ground turkey',
  // Dairy
  'cheese', 'cheeses', 'cheddar', 'mozzarella', 'parmesan', 'butter', 'milk',
  'yogurt', 'yogurts', 'sour cream', 'cream cheese', 'eggs', 'egg', 'feta',
  // Produce
  'tomato', 'tomatoes', 'onion', 'onions', 'garlic', 'pepper', 'peppers',
  'lettuce', 'spinach', 'avocado', 'avocados', 'lime', 'limes', 'lemon', 'lemons',
  'cilantro', 'jalapeño', 'jalapeno', 'jalapenos', 'corn', 'beans', 'bean',
  'potato', 'potatoes', 'carrot', 'carrots', 'celery', 'cucumber', 'cucumbers',
  'mushroom', 'mushrooms', 'broccoli', 'cabbage', 'basil', 'parsley', 'oregano',
  'olive', 'olives', 'banana', 'bananas', 'apple', 'apples', 'berry', 'berries',
  'strawberry', 'strawberries', 'blueberry', 'blueberries',
  // Grains/Bread/Pasta
  'tortilla', 'tortillas', 'bread', 'breads', 'rice', 'pasta', 'pastas',
  'noodle', 'noodles', 'flour', 'chip', 'chips', 'cracker', 'crackers',
  'taco', 'tacos', 'pita', 'pitas', 'biscuit', 'biscuits', 'roll', 'rolls',
  'linguine', 'fettuccine', 'spaghetti', 'penne', 'orzo', 'tortellini',
  // Condiments/Sauces (specific ones)
  'salsa', 'mayo', 'mayonnaise', 'mustard', 'ketchup', 'sriracha',
  'ranch', 'bbq', 'teriyaki', 'marinara', 'alfredo', 'pesto',
  // Baking
  'vanilla', 'chocolate', 'cocoa', 'cookie', 'cookies', 'brownie', 'brownies',
  'cake', 'cakes', 'frosting', 'icing', 'sprinkle', 'sprinkles',
  // Breakfast
  'pancake', 'pancakes', 'waffle', 'waffles', 'syrup', 'cereal', 'oatmeal',
  'granola', 'bagel', 'bagels', 'muffin', 'muffins',
  // Brands often in coupons
  'kraft', 'heinz', 'hellmanns', 'hellmann', 'hidden valley', 'franks',
  'tabasco', 'cholula', 'tostitos', 'doritos', 'mission', 'old el paso',
  'barilla', 'ragu', 'prego', 'classico', 'philadelphia', 'velveeta',
  // Additional common ingredients
  'honey', 'rotel', 'chili',
]);

/**
 * Normalize text for matching by removing measurements, quantities, and special characters
 */
export function normalizeForMatching(text: string): string {
  return text
    .toLowerCase()
    // Remove common measurement terms
    .replace(/\b(cups?|oz|ounces?|lbs?|pounds?|tbsp|tsp|tablespoons?|teaspoons?|cans?|packages?|pkg|bottles?|jars?|bags?|boxes?|containers?|sticks?|slices?|pieces?|cloves?|heads?|bunches?|sprigs?|pinch|dash|handful|gallons?|quarts?|pints?|liters?|ml|g|kg)\b/gi, '')
    // Remove numbers including fractions
    .replace(/\d+(\.\d+)?/g, '')
    .replace(/\d+\/\d+/g, '')
    // Remove special characters except spaces
    .replace(/[^a-z\s]/g, '')
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract keywords from an ingredient, filtering out stopwords
 */
export function getIngredientKeywords(ingredient: string): string[] {
  const normalized = normalizeForMatching(ingredient);
  const words = normalized.split(' ').filter(word => word.length >= MIN_KEYWORD_LENGTH);

  const keywords = new Set<string>();

  words.forEach(word => {
    // Skip stopwords
    if (STOPWORDS.has(word)) return;

    keywords.add(word);

    // Add plural variant if singular
    if (!word.endsWith('s')) {
      keywords.add(word + 's');
    }

    // Add singular variant if plural
    if (word.endsWith('s') && word.length >= MIN_LENGTH_FOR_DEPLURALIZE) {
      keywords.add(word.slice(0, -1));
    }

    // Handle 'ies' -> 'y' (e.g., berries -> berry)
    if (word.endsWith('ies')) {
      keywords.add(word.slice(0, -3) + 'y');
    }

    // Handle 'y' -> 'ies' (e.g., berry -> berries)
    if (word.endsWith('y') && !word.endsWith('ey')) {
      keywords.add(word.slice(0, -1) + 'ies');
    }
  });

  // Filter out any generated words that are stopwords
  return Array.from(keywords).filter(k => !STOPWORDS.has(k));
}

/**
 * Check if a keyword matches as a whole word in text
 */
function matchesWholeWord(text: string, keyword: string): boolean {
  // Use word boundary matching
  const regex = new RegExp(`\\b${keyword}\\b`, 'i');
  return regex.test(text);
}

/**
 * Non-food keywords that indicate a coupon is not for food products
 * Used to filter out false positives
 */
const NON_FOOD_INDICATORS = new Set([
  'toothpaste', 'toothbrush', 'dental', 'floss', 'mouthwash',
  'detergent', 'laundry', 'fabric', 'softener', 'dryer',
  'shampoo', 'conditioner', 'soap', 'body wash', 'lotion',
  'diaper', 'diapers', 'wipes', 'baby wipes',
  'paper towel', 'toilet paper', 'tissue', 'tissues',
  'cleaner', 'cleaning', 'disinfectant', 'bleach',
  'deodorant', 'antiperspirant', 'razor', 'shaving',
  'vitamin', 'supplement', 'medicine', 'medication',
  'pet food', 'dog food', 'cat food', 'cat litter',
]);

/**
 * Check if a coupon is likely for a non-food product
 */
function isNonFoodCoupon(coupon: Coupon): boolean {
  const couponText = [coupon.subtitle, coupon.title, coupon.description, coupon.brand]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return Array.from(NON_FOOD_INDICATORS).some(indicator =>
    couponText.includes(indicator)
  );
}

/**
 * Check if an ingredient matches a coupon
 * Matches on subtitle (product name), title, description, and brand
 */
function matchesCoupon(ingredient: string, coupon: Coupon): boolean {
  // Skip non-food coupons entirely
  if (isNonFoodCoupon(coupon)) return false;

  const keywords = getIngredientKeywords(ingredient);

  if (keywords.length === 0) return false;

  // Match on multiple fields since different providers use different field naming
  const couponText = normalizeForMatching(
    [coupon.subtitle, coupon.title, coupon.description, coupon.brand].filter(Boolean).join(' ')
  );

  // Check if any keyword matches as a whole word
  // ONLY match on known food keywords to avoid false positives
  return keywords.some(keyword => {
    if (!FOOD_KEYWORDS.has(keyword)) return false;
    return matchesWholeWord(couponText, keyword);
  });
}

/**
 * Find all coupons that match any ingredient in the recipe
 */
export function findMatchingCoupons(
  ingredients: RecipeIngredient[],
  coupons: Coupon[]
): Coupon[] {
  if (!ingredients?.length || !coupons?.length) return [];

  const matchingCoupons = new Map<string, Coupon>();

  ingredients.forEach(ingredient => {
    const ingredientText = ingredient.RecipeIngredient;

    coupons.forEach(coupon => {
      const couponId = coupon.offerId || coupon.id;

      // Skip if we already matched this coupon
      if (matchingCoupons.has(couponId)) return;

      if (matchesCoupon(ingredientText, coupon)) {
        matchingCoupons.set(couponId, coupon);
      }
    });
  });

  return Array.from(matchingCoupons.values());
}
